import { isDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import clsx from 'clsx';
import { type ChangeEvent, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import type { Client } from '@/api/user/getCurrentUser.ts';
import { Grid, type GridProps } from '@/App/Grid/Grid.tsx';
import { ZoomedTile } from '@/App/Grid/ZoomedTile.tsx';
import { ClientsTable } from '@/components/ClientSelector/ClientsTable.tsx';
import { useUserContext } from '@/context/UserContext.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { selectActiveTabClientId, selectActiveTabOwner, selectIsActiveTabShared } from '@/store/tabs/selectors.ts';
import { changeClient, removeTab, selectActiveTabId, selectActiveTabZoomedTileId } from '@/store/tabs/slice.ts';

export default function GridAccess({ className }: GridProps) {
  const tabClientId = useAppSelector(selectActiveTabClientId);
  const {
    currentUser: { clients },
  } = useUserContext();

  const hasAccessToTab = useMemo(
    () => clients.some((client) => client.companyId === tabClientId),
    [clients, tabClientId],
  );

  const zoomedTileId = useAppSelector(selectActiveTabZoomedTileId);

  if (hasAccessToTab) {
    if (isDefined(zoomedTileId)) {
      return <ZoomedTile tileId={zoomedTileId} />;
    }
    return <Grid className={className} />;
  }

  return <UnauthorisedTab className={className} />;
}

function UnauthorisedTab({ className }: { className?: string }) {
  const dispatch = useAppDispatch();
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const tabId = useAppSelector(selectActiveTabId);
  const owner = useAppSelector(selectActiveTabOwner);
  const isShared = useAppSelector(selectIsActiveTabShared);

  const canChangeClient = !isShared;

  const onCloseTab = () => {
    if (isDefined(tabId)) {
      sgwtWebAnalytics?.trackEvent('Workspace', 'Close inaccessible tab', tabId);
      dispatch(removeTab(tabId));
    }
  };

  return (
    <div className={clsx(className, 'd-flex flex-column align-items-center justify-content-center gap-2')}>
      <div className="icon icon-xl">lock</div>
      <h1>
        <FormattedMessage id="workspace.inaccessible.title" />
      </h1>

      {isShared ? (
        <FormattedMessage id="workspace.shared.inaccessible.description" values={{ owner }} />
      ) : (
        <FormattedMessage id="workspace.tiles.inaccessible.description" />
      )}

      <div className="mt-3">
        {canChangeClient ? (
          <ClientSelector tabId={tabId} />
        ) : (
          <Button variant="primary" onClick={onCloseTab}>
            <FormattedMessage id="workspace.inaccessible.close-button" />
          </Button>
        )}
      </div>
    </div>
  );
}

interface ClientSelectorProps {
  tabId?: string;
}

function ClientSelector({ tabId }: ClientSelectorProps) {
  const dispatch = useAppDispatch();
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const [searchTerm, setSearchTerm] = useState('');

  const onSelectClient = (clientId: Client['companyId']) => {
    if (isDefined(tabId)) {
      sgwtWebAnalytics?.trackEvent('Workspace', 'Change inaccessible tab client', String(clientId));
      dispatch(changeClient({ tabId, clientId }));
    }
  };

  const onSearchTermChange = (event: ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value);

  return (
    <div className="d-flex flex-column gap-3 align-items-center">
      <div style={{ width: '48rem' }}>
        <div className="input-group mb-3">
          <input
            type="text"
            className="form-control"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={onSearchTermChange}
            aria-label="Search clients"
          />
        </div>
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <ClientsTable searchTerm={searchTerm} onSelectClient={onSelectClient} />
        </div>
      </div>
    </div>
  );
}
